/**
 * Client API pour interagir avec le backend concernant les dépenses.
 * Utilise VITE_API_URL ou '/api' par défaut (pour le reverse proxy).
 */
const API_URL = import.meta.env.VITE_API_URL || '/api';

export interface Expense {
  id: string;
  name: string;
  amount: number;
  category: string;
  date: string;
  type: 'expense' | 'income';
  is_subscription: boolean;
  createdAt: string;
}

// Fonction utilitaire pour éviter la duplication des headers HTTP
const getHeaders = (token: string) => ({
  'Authorization': token,
  'Content-Type': 'application/json',
});

export const expensesApi = {
  /**
   * Récupère la liste des dépenses fixes de l'utilisateur
   */
  async getExpenses(token: string): Promise<Expense[]> {
    const res = await fetch(`${API_URL}/expenses`, {
      headers: getHeaders(token),
    });
    if (!res.ok) {
      throw new Error("Impossible de récupérer la liste des dépenses.");
    }
    return res.json();
  },

  /**
   * Envoie un fichier PDF contenant un relevé, que le backend
   * va analyser via Gemini pour générer des dépenses.
   */
  async uploadPdf(token: string, file: File): Promise<Expense[]> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_URL}/expenses/upload`, {
      method: 'POST',
      headers: {
        'Authorization': token,
        // FormData détermine automatiquement le Content-Type (multipart/form-data)
      },
      body: formData,
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.detail || "Échec de l'analyse du document.");
    }
    return res.json();
  },

  /**
   * Supprime définitivement une dépense
   */
  async deleteExpense(token: string, id: string): Promise<void> {
    const res = await fetch(`${API_URL}/expenses/${id}`, {
      method: 'DELETE',
      headers: getHeaders(token),
    });
    if (!res.ok) {
      throw new Error("Une erreur est survenue lors de la suppression.");
    }
  },

  /**
   * Génère un conseil IA à partir de la liste des dépenses affichée.
   */
  async getAdvice(token: string, expenses: Expense[]): Promise<string> {
    const expense_ids = expenses.map(e => e.id);
    const res = await fetch(`${API_URL}/expenses/advice`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ expense_ids }),
    });
    if (!res.ok) {
      throw new Error("Impossible de générer le conseil IA.");
    }
    const data = await res.json();
    return data.advice;
  }
};
